import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, X, ShoppingBag, ArrowRight, Truck, ShieldCheck, Clock, Lock, Loader2, AlertCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTranslation } from '../hooks/useTranslation';
import PaymentMethods from '../components/PaymentMethods';

export default function Cart() {
  const { items, removeItem, updateQty, total, clear } = useCart();
  const { t, lang } = useTranslation();
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [pendingCheckout, setPendingCheckout] = useState(null); // { checkoutUrl, unavailable }

  const withLocale = (url) => {
    try {
      const u = new URL(url);
      u.searchParams.set('locale', lang || 'fr');
      return u.toString();
    } catch {
      return url;
    }
  };

  const handleCheckout = async () => {
    setCheckingOut(true);
    setCheckoutError(null);
    setPendingCheckout(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shopify/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items.map((i) => ({ handle: i.slug, quantity: i.qty, variantId: i.variantId || null })) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('cart.errorGeneric'));

      if (data.unavailable && data.unavailable.length > 0) {
        setPendingCheckout(data);
        setCheckingOut(false);
      } else {
        window.location.href = withLocale(data.checkoutUrl);
      }
    } catch (err) {
      setCheckoutError(err.message || t('cart.errorGeneric'));
      setCheckingOut(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <ShoppingBag className="w-10 h-10 text-gold mx-auto" strokeWidth={1.2} />
          <h1 className="font-serif text-4xl md:text-5xl font-light mt-6">{t('cart.empty')}</h1>
          <p className="text-[#666] mt-4">{t('cart.emptyText')}</p>
          <Link to="/collections" className="inline-flex items-center gap-3 mt-10 px-10 py-4 bg-[#1A1A1A] text-white text-[11px] tracking-luxury uppercase hover:bg-gold transition-colors duration-500">
            {t('cart.continueShopping')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main>
      <section className="pt-20 pb-10 lg:pt-24 lg:pb-14 bg-ivory text-center">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-[11px] tracking-luxury uppercase text-gold mb-4">{t('cart.label')}</div>
          <h1 className="font-serif text-5xl md:text-6xl font-light">{t('cart.title')}</h1>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 divide-y divide-[#EAE4D8] border-y border-[#EAE4D8]">
            {items.map((item) => {
              const key = item.variantId || item.slug;
              return (
              <div key={key} className="py-6 flex gap-4 md:gap-6">
                <Link to={`/produit/${item.slug}`} className="w-32 md:w-36 aspect-square bg-white border border-[#EAE4D8] flex-shrink-0 flex items-center justify-center" data-testid="cart-item-image">
                  <img src={item.image} alt={item.name} className="w-full h-full object-contain object-center p-3" />
                </Link>
                <div className="flex-1 flex flex-col justify-between">
                  <div className="flex justify-between gap-4">
                    <div>
                      <Link to={`/produit/${item.slug}`} className="font-serif text-lg md:text-xl hover:text-gold">{item.name}</Link>
                      {item.disponibilite && (
                        <div className="mt-1" data-testid={`cart-item-variant-${key}`}>
                          <span className={`inline-block text-[10px] tracking-luxury uppercase px-2 py-0.5 border ${item.disponibilite === 'precommande' ? 'border-gold text-gold' : 'border-[#999] text-[#666]'}`}>
                            {item.disponibilite === 'precommande' ? t('productDetail.preorderOption') : t('productDetail.inStockOption')}
                          </span>
                        </div>
                      )}
                      <div className="text-xs text-[#888] tracking-luxury uppercase mt-1">{t('cart.unitPrice')} : {item.price} €</div>
                    </div>
                    <button onClick={() => removeItem(key)} className="text-[#999] hover:text-[#1A1A1A]" aria-label={t('cart.remove')} data-testid={`cart-remove-${key}`}><X className="w-4 h-4" /></button>
                  </div>
                  <div className="flex justify-between items-end mt-4">
                    <div className="flex items-center border border-[#EAE4D8]">
                      <button onClick={() => updateQty(key, item.qty - 1)} className="p-2 hover:bg-cream" aria-label={t('productDetail.decrease')}><Minus className="w-3 h-3" /></button>
                      <span className="w-8 text-center text-sm" data-testid={`cart-qty-${key}`}>{item.qty}</span>
                      <button onClick={() => updateQty(key, item.qty + 1)} className="p-2 hover:bg-cream" aria-label={t('productDetail.increase')}><Plus className="w-3 h-3" /></button>
                    </div>
                    <div className="font-serif text-lg">{item.price * item.qty} €</div>
                  </div>
                </div>
              </div>
              );
            })}
            <div className="pt-6">
              <button onClick={clear} className="text-[11px] tracking-luxury uppercase text-[#888] hover:text-[#1A1A1A]" data-testid="cart-clear-button">{t('cart.clear')}</button>
            </div>
          </div>

          {/* Summary */}
          <aside className="bg-cream p-8 h-fit lg:sticky lg:top-28">
            <div className="text-[11px] tracking-luxury uppercase text-gold mb-4">{t('cart.recap')}</div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span>{t('cart.subtotal')}</span><span>{total} €</span></div>
              <div className="flex justify-between text-[#666]"><span>{t('cart.shipping')}</span><span>{t('cart.free')}</span></div>
              <div className="h-px bg-[#EAE4D8] my-4" />
              <div className="flex justify-between font-serif text-xl"><span>{t('cart.total')}</span><span>{total} €</span></div>
            </div>
            <button
              onClick={handleCheckout}
              disabled={checkingOut}
              data-testid="checkout-button"
              className="w-full mt-8 bg-[#1A1A1A] text-white px-8 py-4 text-[11px] tracking-luxury uppercase hover:bg-gold transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {checkingOut ? (<><Loader2 className="w-4 h-4 animate-spin" /> {t('cart.connecting')}</>) : t('cart.checkoutButton')}
            </button>
            {checkoutError && (
              <div className="mt-4 text-[11px] text-red-700 flex items-start gap-2" data-testid="checkout-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{checkoutError}</span>
              </div>
            )}
            {pendingCheckout && (
              <div className="mt-4 p-4 bg-white border border-[#EAE4D8] text-[11px] leading-relaxed" data-testid="checkout-unavailable-notice">
                <span className="text-[#666]">{t('cart.unavailableNotice')}</span>
                <button
                  onClick={() => { window.location.href = withLocale(pendingCheckout.checkoutUrl); }}
                  data-testid="checkout-continue-button"
                  className="mt-3 w-full bg-[#1A1A1A] text-white px-6 py-3 text-[11px] tracking-luxury uppercase hover:bg-gold transition-colors duration-500"
                >
                  {t('cart.continuePayment')}
                </button>
              </div>
            )}
            <div className="mt-6 text-[10px] tracking-luxury uppercase text-[#888] text-center">
              {t('cart.securePaymentGuarantee')}
            </div>
          </aside>
        </div>
      </section>

      {/* Trust badges */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 grid grid-cols-4 gap-4 border-t border-[#EAE4D8] pt-12">
          {[
            { icon: Truck, t: t('cart.badgeLivraison'), s: t('cart.badgeLivraisonSub') },
            { icon: ShieldCheck, t: t('cart.badgeGarantie'), s: t('cart.badgeGarantieSub') },
            { icon: Clock, t: t('cart.badgeExpedition'), s: t('cart.badgeExpeditionSub') },
            { icon: Lock, t: t('cart.badgePaiement'), s: t('cart.badgePaiementSub') }
          ].map((b, i) => (
            <div key={i} className="text-center">
              <b.icon className="w-5 h-5 text-gold mx-auto" strokeWidth={1.2} />
              <div className="text-[10px] tracking-luxury uppercase mt-2">{b.t}</div>
              <div className="text-[10px] text-[#888] mt-1">{b.s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Payment methods */}
      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-4">
          <PaymentMethods />
        </div>
      </section>
    </main>
  );
}
